import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext, getIn, Formik, Form } from 'formik';
import * as Yup from 'yup';
import { FarmRiskType } from '@ourbranch/lookups';

import { Button } from 'core/components/button';
import Field from 'core/components/form/form.v2';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { useToast } from 'core/components/toast';
import { getPropertyTypeOptions, getResidenceUsageOptions } from 'common/helpers/farm-helpers';
import { useClearInvalidRiskOptions } from 'common/helpers/use-clear-invalid-risk-options';

import useStyles from './edit-risk-modal.styles';

// Inner component to use the clearing hook (hooks can't be called inside render props)
const EditRiskFormFields = ({ formValues, currentFormType, classes, handleSubmit, isValid }) => {
  const { setFieldValue } = useFormikContext();
  const formType = formValues.type;

  // Clear invalid residenceUsage when risk type changes
  // propertyType is disabled in this modal, so we only need to clear residenceUsage
  useClearInvalidRiskOptions({
    riskType: formType,
    residenceUsage: formValues.residenceUsage,
    setFieldValue,
    residenceUsagePath: 'residenceUsage'
  });

  const isDwellingOrRentersRiskType = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(
    formType
  );

  return (
    <Form>
      <Field xs={12} type="string" name="description" id="description" label="Risk name" mode="light" />
      <Field
        xs={12}
        type="select"
        name="type"
        id="type"
        mode="light"
        label="Dwelling risk form type"
        options={riskTypeOptionsLookup[currentFormType]}
      />
      {isDwellingOrRentersRiskType && (
        <>
          <Field
            key={`propertyType-${formType}`}
            xs={12}
            type="select"
            name="propertyType"
            id="propertyType"
            mode="light"
            label="Structure Category"
            options={getPropertyTypeOptions(formType)}
            disabled
          />
          <Field
            key={`residenceUsage-${formType}`}
            xs={12}
            type="select"
            name="residenceUsage"
            id="residenceUsage"
            mode="light"
            label="Residence Type/Usage"
            options={getResidenceUsageOptions(formType)}
            disabled={formType === FarmRiskType.Fo4}
          />
        </>
      )}
      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          onClick={handleSubmit}
          className={classes.buttons}
          disabled={!isValid}
        >
          Save changes
        </Button>
      </div>
    </Form>
  );
};

const riskTypeOptionsLookup = {
  FO1: [
    { id: FarmRiskType.Fo1, value: 'FO-1 Basic Form' },
    { id: FarmRiskType.Fo2, value: 'FO-2 Broad Form' },
    { id: FarmRiskType.Fo3, value: 'FO-3 Special Form' }
  ],
  FO2: [
    { id: FarmRiskType.Fo1, value: 'FO-1 Basic Form' },
    { id: FarmRiskType.Fo2, value: 'FO-2 Broad Form' },
    { id: FarmRiskType.Fo3, value: 'FO-3 Special Form' }
  ],
  FO3: [
    { id: FarmRiskType.Fo1, value: 'FO1 Basic Form' },
    { id: FarmRiskType.Fo2, value: 'FO2 Broad Form' },
    { id: FarmRiskType.Fo3, value: 'FO3 Special Form' }
  ],
  FO4: [{ id: FarmRiskType.Fo4, value: 'FO-4 Renters Form' }],
  // FO5: [], will need to add FO5 when we add it
  FO6: [
    { id: FarmRiskType.Fo6Structures, value: 'FO-6 Structures Form' },
    { id: FarmRiskType.Fo6Scheduled, value: 'FO-6 Scheduled Form' },
    { id: FarmRiskType.Fo6Unscheduled, value: 'FO-6 Unscheduled Form' }
  ]
};

const EditRiskModal = ({ onClose, fieldName }) => {
  const classes = useStyles();
  const toast = useToast();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();

  const currentFormType = getIn(values, `${fieldName}.type`);
  const currentPropertyType = getIn(values, `${fieldName}.building.propertyType`);
  const currentResidenceUsage = getIn(values, `${fieldName}.building.residenceUsage`);

  const validationSchema = Yup.object().shape({
    description: Yup.string().required('Risk name is required'),
    type: Yup.string().required('Dwelling risk form type is required'),
    propertyType: Yup.string()
      .when('type', {
        is: (type) => [FarmRiskType.Fo3, FarmRiskType.Fo4].includes(type),
        then: Yup.string().required('Structure category is required'),
        otherwise: Yup.string()
      })
      .test('no-mobile-home-for-fo3', 'Mobile Home is not allowed for FO-3', function (value) {
        const { type } = this.parent;
        return !(type === FarmRiskType.Fo3 && value === 'MH');
      }),
    residenceUsage: Yup.string()
      .when(['type', 'propertyType'], {
        is: (type, propertyType) => {
          if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(type)) {
            return false;
          }

          // Barndominium ('B') doesn't require residence usage
          // Mobile Home ('MH') is not allowed for FO-3 (enforced by propertyType validation)
          if (type === FarmRiskType.Fo3 && propertyType === 'B') {
            return false;
          }

          return true;
        },
        then: Yup.string().required('Residence type/usage is required'),
        otherwise: Yup.string()
      })
      .test('no-vacant-for-fo2-fo3', 'Vacant is not allowed for FO-2/FO-3', function (value) {
        const { type } = this.parent;
        return !([FarmRiskType.Fo2, FarmRiskType.Fo3].includes(type) && value === 'V');
      })
  });
  const initialValues = {
    description: getIn(values, `${fieldName}.description`),
    type: currentFormType,
    propertyType: currentPropertyType,
    residenceUsage: currentResidenceUsage
  };

  const handleSubmit = async (modalValues) => {
    const originalFormType = currentFormType;
    const newFormType = modalValues.type;
    const riskName = modalValues.description;

    setFieldValue(`${fieldName}.description`, modalValues.description);
    setFieldTouched(`${fieldName}.description`, true);
    setFieldValue(`${fieldName}.type`, modalValues.type);
    setFieldTouched(`${fieldName}.type`, true);
    setFieldValue(`${fieldName}.building.residenceUsage`, modalValues.residenceUsage);
    setFieldTouched(`${fieldName}.building.residenceUsage`, true);

    if (originalFormType !== newFormType) {
      // Automatically adjust Coverage D when switching between FO types to ensure compliance with rules
      const coverageA = getIn(values, `${fieldName}.coverages.coverageA`);
      const coverageD = getIn(values, `${fieldName}.coverages.coverageD`);
      const residenceUsage = modalValues.residenceUsage;

      if ([FarmRiskType.Fo2, FarmRiskType.Fo3].includes(newFormType)) {
        // Switching to FO2/FO3: If residence is Primary/Seasonal, apply 20% rule
        if (['P', 'S'].includes(residenceUsage) && coverageA) {
          const calculatedCoverageD = Math.round(coverageA * 0.2);
          setFieldValue(`${fieldName}.coverages.coverageD`, calculatedCoverageD);
          setFieldTouched(`${fieldName}.coverages.coverageD`, true);
        }
      } else if (newFormType === FarmRiskType.Fo1) {
        // Switching to FO1: Ensure Coverage D doesn't exceed $3,000 limit
        if (coverageD > 3000) {
          setFieldValue(`${fieldName}.coverages.coverageD`, 3000);
          setFieldTouched(`${fieldName}.coverages.coverageD`, true);
        }
      }

      const newFormLabel =
        riskTypeOptionsLookup[originalFormType]?.find((option) => option.id === newFormType)?.value || newFormType;

      toast.notify({
        type: 'success',
        message: `We've updated ${riskName} to ${newFormLabel}. Please review property details and coverages for accuracy.`
      });
    }

    onClose();
  };

  return (
    <BaseDialog className={classes.modal} onClose={onClose} open>
      <div className={classes.container}>
        <Label className={classes.mainLabel} type="green75Big">
          Modify dwelling or structure risk
        </Label>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit, isValid, values: formValues }) => (
            <EditRiskFormFields
              formValues={formValues}
              currentFormType={currentFormType}
              classes={classes}
              handleSubmit={handleSubmit}
              isValid={isValid}
            />
          )}
        </Formik>
      </div>
    </BaseDialog>
  );
};

EditRiskModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  fieldName: PropTypes.string
};

export default observer(EditRiskModal);
